import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CreditCard, History, Plus, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import InstitutionNavbar from '../components/instituteNavbar';
import Footer from '../components/Footer';
import PricingTable from "../components/PricingTable";

const WalletPage = () => {
    const [balance, setBalance] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [processing] = useState(false);
    const [showPricing, setShowPricing] = useState(false);

    useEffect(() => {
        fetchWalletData();
    }, []);

    const fetchWalletData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/credits/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                setBalance(response.data.currentCredits);
                setTransactions(response.data.data);
            }
        } catch (err) {
            console.error("Failed to fetch wallet data:", err);
            setError("Failed to load wallet details.");
        } finally {
            setLoading(false);
        }
    };
    const handleAddCredits = () => {
        setShowPricing(true);

        setTimeout(() => {
            const el = document.getElementById("pricing-table");
            if (el) {
                el.scrollIntoView({ behavior: "smooth" });
            }
        }, 200);
    };



    return (
        <div className="flex flex-col min-h-screen bg-gray-50 font-sans">
            <InstitutionNavbar />

            <div className="flex-grow pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto w-full">
                <h1 className="text-3xl pt-5 font-bold text-gray-800 mb-8">My Wallet</h1>

                {/* Balance Card Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">

                    {/* Balance Display */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
                        {/* Decorative circles */}
                        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>
                        <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>

                        <div className="relative z-10">
                            <div className="flex items-center gap-3 mb-2">
                                <CreditCard className="w-6 h-6 opacity-80" />
                                <span className="text-lg font-medium opacity-90">Current Balance</span>
                            </div>
                            <div className="text-5xl font-bold mb-6">
                                {loading ? '...' : balance.toLocaleString()} <span className="text-2xl font-normal opacity-70">Credits</span>
                            </div>
                            <div className="text-sm opacity-75">
                                Use credits to unlock candidate profiles and post jobs.
                            </div>
                        </div>
                    </div>

                    {/* Top Up Action */}
                    <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 flex flex-col justify-center items-center text-center">
                        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                            <Plus className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Need More Credits?</h3>
                        <p className="text-gray-500 mb-6 max-w-sm">
                            Purchase additional credits instantly to continue finding the best talent for your institution.
                        </p>
                        <button
                            onClick={handleAddCredits}
                            disabled={processing || loading}
                            className={`px-8 py-3 rounded-xl font-bold text-white shadow-lg transform transition-all hover:-translate-y-1 ${processing ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 hover:shadow-green-500/30'}`}
                        >
                            View Plans
                        </button>
                    </div>
                </div>
                {/* Enterprise Pricing Table (shown after clicking Add Credits) */}
                {showPricing && (
                    <div id="pricing-table" className="mb-14 mt-10">

                        <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">
                            Upgrade Your Credit Plan
                        </h2>

                        <PricingTable
                            plans={[
                                {
                                    name: "Basic",
                                    oldPrice: "₹9,999",
                                    price: "₹4,999",
                                    off: "50% off",
                                    ideal: "1–2 hirings",
                                    searches: "Unlimited",
                                    views: 100,
                                    jobs: 3,
                                    emails: 500,
                                    validity: "30 days",
                                    highlight: false
                                },
                                {
                                    name: "Standard",
                                    oldPrice: "₹11,999",
                                    price: "₹5,999",
                                    off: "50% off",
                                    ideal: "5–8 hirings",
                                    searches: "Unlimited",
                                    views: 230,
                                    jobs: 10,
                                    emails: 1500,
                                    validity: "30 days",
                                    highlight: false
                                },
                                {
                                    name: "Premium",
                                    oldPrice: "₹14,999",
                                    price: "₹7,499",
                                    off: "50% off",
                                    note: "40% users bought this",
                                    ideal: "10–12 hirings",
                                    searches: "Unlimited",
                                    views: 450,
                                    jobs: 25,
                                    emails: 3000,
                                    validity: "30 days",
                                    highlight: true
                                },
                                {
                                    name: "Premium Pro",
                                    oldPrice: "₹29,999",
                                    price: "₹14,999",
                                    off: "50% off",
                                    ideal: "25–30 hirings",
                                    searches: "Unlimited",
                                    views: 1175,
                                    jobs: 30,
                                    emails: 9000,
                                    validity: "90 days",
                                    highlight: false
                                }
                            ]}
                            onBuy={() => alert("Contact shri@evolvetech.in")}
                        />
                    </div>
                )}



                {/* Transaction History section Debit/Credit */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <History className="w-5 h-5 text-gray-500" />
                            Transaction History
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                    <th className="px-6 py-4 font-semibold">Date</th>
                                    <th className="px-6 py-4 font-semibold">Description</th>
                                    <th className="px-6 py-4 font-semibold">Type</th>
                                    <th className="px-6 py-4 font-semibold text-right">Amount</th>
                                    <th className="px-6 py-4 font-semibold text-right">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400">Loading history...</td>
                                    </tr>
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-400 flex flex-col items-center">
                                            <AlertCircle className="w-8 h-8 mb-2 opacity-50" />
                                            No transactions found.
                                        </td>
                                    </tr>
                                ) : (
                                    transactions.map((txn, index) => (
                                        <tr key={txn._id || index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {new Date(txn.createdAt).toLocaleDateString()} <span className="text-xs text-gray-400 ml-1">{new Date(txn.createdAt).toLocaleTimeString()}</span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-800 font-medium">
                                                {txn.description || 'N/A'}
                                                {txn.referenceId && <div className="text-xs text-gray-400 font-normal mt-0.5">Ref: {txn.referenceId}</div>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${txn.type === 'credit'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {txn.type === 'credit' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                                                    {txn.type === 'credit' ? 'Credit' : 'Debit'}
                                                </span>
                                            </td>
                                            <td className={`px-6 py-4 text-sm font-bold text-right ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                                {txn.type === 'credit' ? '+' : '-'}{txn.amount}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 text-right font-medium">
                                                {txn.balanceAfter}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
            <Footer />
        </div>
    );
};

export default WalletPage;
